import React from 'react';
import { css } from '@emotion/react';
import { useFormContext } from 'react-hook-form';

import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import CustomRadioGroup from 'components/custom-radio-group';
import useAiRolePlayAvatarOptions from '../../hooks/use-ai-role-play-avatar-options';
import AvatarOption, { Avatar } from './avatar-option';

const avatarListStyles = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${halfSpacing}px;
`;

const avatarOptionContainerStyles = css`
  cursor: pointer;
`;

const AvatarList = () => {
  const params = useLecturePageParams();
  const avatarOptions = useAiRolePlayAvatarOptions(params.catalogId);
  const { setValue, watch } = useFormContext();
  const selectedAvatarKey = watch('voiceOptionKey');

  React.useEffect(() => {
    if (!selectedAvatarKey && avatarOptions.length) {
      const first = avatarOptions[0];
      setValue('voiceOptionKey', first.key);
      setValue('aiCharacterFace', { source: 'preset', presetKey: first.facePresetKey }, { shouldDirty: true });
    }
  }, [selectedAvatarKey, avatarOptions, setValue]);

  const avatars: Avatar[] = React.useMemo(
    () => avatarOptions.map((option) => ({
      id: option.key,
      name: option.name,
      displayUrl: option.displayUrl,
      gender: option.gender,
      facePresetKey: option.facePresetKey,
    })),
    [avatarOptions],
  );

  const handleSelect = (avatar: Avatar) => {
    setValue('voiceOptionKey', avatar.id, { shouldDirty: true });
    setValue('aiCharacterFace', { source: 'preset', presetKey: avatar.facePresetKey }, { shouldDirty: true });
  };

  return (
    <CustomRadioGroup
      name='voiceOptionKey'
      className='mt-4'
      css={avatarListStyles}
      radios={avatars.map((avatar) => ({ value: avatar.id, avatar }))}
      activeValue={selectedAvatarKey}
      onChange={(avatarKey) => {
        const avatar = avatars.find((a) => a.id === avatarKey);
        if (avatar) handleSelect(avatar);
      }}
      optionContainerStyle={avatarOptionContainerStyles}
      radioInputTabIndex={-1}
      renderRadioButton={({ avatar, checked }) => (
        <AvatarOption
          avatar={avatar}
          isSelected={checked}
          onSelect={() => handleSelect(avatar)}
        />
      )}
    />
  );
};

export default AvatarList;
